import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, CheckCircle, RotateCcw, Clock,
  Plus, ChevronLeft, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const merchantName = (user?.fullName || user?.username || 'التاجر').trim();
  const [stats, setStats] = useState<any>({});
  const [shipments, setShipments] = useState<any[]>([]);
  const [pendingShipments, setPendingShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const formatNumber = (num: number = 0) =>
    new Intl.NumberFormat('ar-EG').format(num || 0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [statsRes, recentRes, pendingRes] = await Promise.all([
          api.get('/api/merchant-dashboard/stats'),
          api.get('/api/merchant-dashboard'),
          api.get('/api/merchant-dashboard/my-pending')
        ]);
        setStats(statsRes.data?.data || {});
        setShipments(recentRes.data?.data?.recentShipments || []);
        setPendingShipments(pendingRes.data?.data || []);
      } catch (e) {
        console.error('Dashboard error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalShipped = (stats.delivered || 0) + (stats.returned || 0) + (stats.outForDelivery || 0) + (stats.pending || 0);
  const pieData = totalShipped > 0 ? [
    { label: 'تم التسليم', value: stats.delivered || 0, color: '#22c55e' },
    { label: 'قيد التوصيل', value: stats.outForDelivery || 0, color: '#3b82f6' },
    { label: 'في المخزن', value: stats.pending || 0, color: '#f59e0b' },
    { label: 'مرتجع', value: stats.returned || 0, color: '#ef4444' },
  ] : [];

  const renderDonut = () => {
    const total = pieData.reduce((s, d) => s + d.value, 0);
    if (total === 0) return (
      <div className="flex items-center justify-center h-[140px] text-gray-400 text-sm">
        لا توجد بيانات
      </div>
    );
    let acc = 0;
    const r = 50;
    const c = 2 * Math.PI * r;
    return (
      <div className="relative w-[140px] h-[140px] mx-auto">
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r={r} fill="none" stroke="#e5e7eb" strokeWidth="16" />
          {pieData.map((d, i) => {
            const dash = (d.value / total) * c;
            const offset = -acc;
            acc += dash;
            return (
              <circle key={i} cx="70" cy="70" r={r} fill="none" stroke={d.color}
                strokeWidth="16" strokeDasharray={`${dash} ${c}`} strokeDashoffset={offset}
                strokeLinecap="round" transform="rotate(-90 70 70)" />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-gray-900">{formatNumber(total)}</span>
          <span className="text-xs text-gray-500">الإجمالي</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-lg font-medium">
        جاري تحميل لوحة التحكم...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مرحبًا، {merchantName}</h1>
          <p className="text-sm text-gray-500 mt-1">لوحة تحكم التاجر ومتابعة الشحنات والإيرادات</p>
        </div>
        <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => navigate('/shipments/new')}>
          <Plus className="ml-2 w-4 h-4" />
          إنشاء شحنة
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'الشحنات الجديدة', value: stats.total || 0, icon: Package, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'قيد التوصيل', value: stats.outForDelivery || 0, icon: Truck, color: 'text-cyan-600', bg: 'bg-cyan-50' },
          { label: 'تم التسليم', value: stats.delivered || 0, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'المرتجعات', value: stats.returned || 0, icon: RotateCcw, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'بانتظار الموافقة', value: stats.awaitingApproval || 0, icon: Clock, color: 'bg-orange-50 text-orange-600', bg: 'bg-orange-50' },
        ].map((s, i) => (
          <Card key={i} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
            if (s.label === 'بانتظار الموافقة') navigate('/shipments/pending');
            else navigate('/shipments');
          }}>
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <div className={`w-12 h-12 rounded-full ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-6 h-6 ${s.color}`} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(s.value)}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">توزيع حالات الشحنات</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {renderDonut()}
              <div className="flex flex-col gap-3 text-sm">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                    <span className="text-gray-600">{d.label}</span>
                    <span className="font-bold text-gray-900 mr-auto">{d.value}</span>
                    <span className="text-xs text-gray-400">({Math.round((d.value / (pieData.reduce((s, x) => s + x.value, 0) || 1)) * 100)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              بانتظار موافقة الإدارة
            </CardTitle>
            <span className="text-xs text-gray-400 cursor-pointer hover:text-orange-600" onClick={() => navigate('/shipments/pending')}>
              عرض الكل
            </span>
          </CardHeader>
          <CardContent>
            {pendingShipments.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">لا توجد شحنات بانتظار الموافقة</div>
            ) : (
              <div className="space-y-3">
                {pendingShipments.slice(0, 5).map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-bold text-sm text-gray-900">{s.trackingNumber}</div>
                      <div className="text-xs text-gray-500">{s.receiverName} - {s.receiverCity}</div>
                    </div>
                    <span className="px-2 py-1 rounded-md text-xs font-medium bg-orange-50 text-amber-700 border border-amber-200">
                      بانتظار الموافقة
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-medium">آخر الشحنات</CardTitle>
          <span className="text-xs text-gray-400 cursor-pointer hover:text-orange-600 flex items-center gap-1" onClick={() => navigate('/shipments')}>
            عرض الكل <ChevronLeft className="w-3 h-3" />
          </span>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-right text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="p-3 font-medium">رقم التتبع</th>
                  <th className="p-3 font-medium">المستلم</th>
                  <th className="p-3 font-medium">المدينة</th>
                  <th className="p-3 font-medium">الحالة</th>
                  <th className="p-3 font-medium">التاريخ</th>
                </tr>
              </thead>
              <tbody>
                {shipments.length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-gray-400">لا توجد شحنات</td></tr>
                ) : (
                  shipments.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/shipments/${s.id}`)}>
                      <td className="p-3 font-mono text-xs text-gray-900">{s.trackingNumber}</td>
                      <td className="p-3 text-gray-700">{s.receiverName}</td>
                      <td className="p-3 text-gray-500">{s.receiverCity}</td>
                      <td className="p-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${
                          s.statusName === 'Delivered' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          s.statusName === 'Pending' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                          s.statusName === 'Out for delivery' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {s.statusName === 'Delivered' ? 'تم التسليم' :
                           s.statusName === 'Pending' ? 'قيد الانتظار' :
                           s.statusName === 'Out for delivery' ? 'قيد التوصيل' :
                           s.statusName || '—'}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-gray-500">
                        {s.createdAt ? new Date(s.createdAt).toLocaleDateString('ar-EG') : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
